import { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { getApiUrl } from '../../config/api';

// Cache for storing fetched profile data per user
const profileCache = new Map();
const lastProfileFetchTimes = new Map();
const PROFILE_CACHE_DURATION = 10 * 60 * 1000; // 10 minutes cache duration for profiles

const useProfile = (userId) => {
  const [profileData, setProfileData] = useState({data: { firstName: '', lastName: '', role: '', city: ''} });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const isInitialMount = useRef(true);

  const fetchProfile = useCallback(async (forceRefresh = false) => {
    if (!userId) return;
    
    // Check if we have valid cached data
    const now = Date.now();
    const lastFetchTime = lastProfileFetchTimes.get(userId) || 0;
    
    if (!forceRefresh && profileCache.has(userId) && (now - lastFetchTime) < PROFILE_CACHE_DURATION) {
      setProfileData(profileCache.get(userId));
      setLoading(false);
      setError(null);
      return;
    }

    setLoading(true);
    setError(null);
    
    try {
      const response = await axios.get(getApiUrl(`/hwu/profile/${userId}`));
      
      // Cache the fetched data
      profileCache.set(userId, response.data);
      lastProfileFetchTimes.set(userId, now);
      
      setProfileData(response.data);
      // Debug log removed — was leaking full profile blob (PII) into console.
    } catch (err) {
      setError(err.message || 'Failed to fetch profile data.');
      
      // If there's an error but we have cached data, use that instead
      if (profileCache.has(userId)) {
        setProfileData(profileCache.get(userId));
      }
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    // Only fetch on initial mount or if cache is expired
    if (isInitialMount.current || !profileCache.has(userId) || (Date.now() - (lastProfileFetchTimes.get(userId) || 0)) >= PROFILE_CACHE_DURATION) {
      fetchProfile();
      isInitialMount.current = false;
    } else {
      // Use cached data immediately
      setProfileData(profileCache.get(userId));
      setLoading(false);
      setError(null);
    }
  }, [userId, fetchProfile]);

  // Function to refresh profile (force fresh fetch)
  const refreshProfile = useCallback(async () => {
    if (userId) {
      await fetchProfile(true);
    }
  }, [userId, fetchProfile]);

  // Function to clear cache for a specific user
  const clearCache = useCallback(() => {
    if (userId) {
      profileCache.delete(userId);
      lastProfileFetchTimes.delete(userId);
    }
  }, [userId]);

  // Function to clear all profile cache
  const clearAllCache = useCallback(() => {
    profileCache.clear();
    lastProfileFetchTimes.clear();
  }, []);

  return { 
    profileData, 
    loading, 
    error, 
    refreshProfile,
    clearCache,
    clearAllCache
  };
};

export default useProfile;
